import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useParticipantCount } from '../context/ParticipantContext';

const FAQSection = () => {
  const { language } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);
  const { formatted: participantCount } = useParticipantCount();

  // FAQ content optimized for AI citation - self-contained, specific answers
  const faqs = language === 'nl' ? [
    {
      question: 'Wat is AI voor HR en waarom is het belangrijk?',
      answer: 'AI voor HR (Artificial Intelligence voor Human Resources) is de toepassing van kunstmatige intelligentie technologieën om HR-processen te automatiseren en verbeteren. Dit omvat toepassingen zoals CV-screening, chatbots voor medewerkersvragen, voorspellende analyses voor personeelsverloop, en gepersonaliseerde leertrajecten. Volgens onderzoek kunnen organisaties met AI in HR tot 40% tijdsbesparing realiseren op administratieve taken, terwijl de kwaliteit van besluitvorming verbetert door data-gedreven inzichten.'
    },
    {
      question: 'Hoe meet ik de AI-gereedheid van mijn HR-team?',
      answer: `De AI-gereedheid van een HR-team wordt gemeten aan de hand van 4 dimensies: Governance (beleid en ethiek), Strategie (visie en roadmap), Technologie & Data (infrastructuur en datakwaliteit), en Mensen & Change (vaardigheden en veranderbereidheid). De Humanistix AI for HR Quickscan evalueert deze dimensies in 4 minuten en vergelijkt uw score met ${participantCount} andere organisaties. U ontvangt direct een gepersonaliseerd rapport met concrete aanbevelingen.`
    },
    {
      question: 'Wat zijn de beste AI use cases voor HR in 2025?',
      answer: 'De meest impactvolle AI use cases voor HR in 2025 zijn: (1) Geautomatiseerde CV-screening die tot 75% tijdsbesparing oplevert, (2) AI-chatbots voor HR-vragen die 24/7 beschikbaar zijn, (3) Voorspellende analyses voor personeelsverloop met 85% nauwkeurigheid, (4) Gepersonaliseerde leertrajecten gebaseerd op skill gaps, en (5) Geautomatiseerde onboarding workflows. Deze toepassingen zijn geschikt voor organisaties in alle sectoren en bedrijfsgroottes.'
    },
    {
      question: 'Hoe verschilt Humanistix van andere AI voor HR platforms?',
      answer: `Humanistix onderscheidt zich door: (1) Een 100% gratis quickscan die in 4 minuten voltooid is, (2) Real-time benchmarking tegen ${participantCount} organisaties in uw sector, (3) Een uitgebreide use case hub met praktische implementatiegidsen, (4) Focus op de Europese markt met naleving van de EU AI Act en AVG/GDPR, en (5) Begeleiding door gecertificeerde AI-trainers met hands-on workshop ervaring. Wij geloven dat elk HR-team toegang moet hebben tot AI-kennis, daarom is onze basis quickscan altijd gratis.`
    },
    {
      question: 'Is AI in HR veilig en ethisch verantwoord?',
      answer: 'AI in HR kan veilig en ethisch worden toegepast mits organisaties een gedegen governance framework hebben. Dit omvat: duidelijk AI-beleid met richtlijnen voor verantwoord gebruik, "human in the loop" bij belangrijke beslissingen zoals aanname en ontslag, transparantie over wanneer AI wordt ingezet, naleving van de EU AI Act die HR-toepassingen als "hoog-risico" classificeert, en regelmatige audits op bias en discriminatie. Humanistix helpt organisaties dit framework op te zetten via onze governance quickscans en workshops.'
    },
    {
      question: 'Hoeveel kost het om AI in HR te implementeren?',
      answer: 'De kosten van AI-implementatie in HR variëren sterk: eenvoudige chatbots beginnen bij €50-200/maand, terwijl enterprise recruitment AI €500-2000/maand kan kosten. De ROI is echter significant: organisaties rapporteren gemiddeld 30-50% tijdsbesparing op recruitment, 25% reductie in verloop door betere matching, en 40% snellere onboarding. De Humanistix quickscan is gratis en helpt u bepalen welke AI-investeringen het meeste rendement opleveren voor uw specifieke situatie.'
    }
  ] : [
    {
      question: 'What is AI for HR and why is it important?',
      answer: 'AI for HR (Artificial Intelligence for Human Resources) is the application of artificial intelligence technologies to automate and improve HR processes. This includes applications such as CV screening, chatbots for employee queries, predictive analytics for staff turnover, and personalized learning paths. According to research, organizations using AI in HR can achieve up to 40% time savings on administrative tasks while improving decision quality through data-driven insights.'
    },
    {
      question: 'How do I measure my HR team\'s AI readiness?',
      answer: `HR team AI readiness is measured across 4 dimensions: Governance (policy and ethics), Strategy (vision and roadmap), Technology & Data (infrastructure and data quality), and People & Change (skills and change readiness). The Humanistix AI for HR Quickscan evaluates these dimensions in 4 minutes and compares your score with ${participantCount} other organizations. You receive an instant personalized report with concrete recommendations.`
    },
    {
      question: 'What are the best AI use cases for HR in 2025?',
      answer: 'The most impactful AI use cases for HR in 2025 are: (1) Automated CV screening delivering up to 75% time savings, (2) AI chatbots for HR questions available 24/7, (3) Predictive analytics for staff turnover with 85% accuracy, (4) Personalized learning paths based on skill gaps, and (5) Automated onboarding workflows. These applications are suitable for organizations across all industries and company sizes.'
    },
    {
      question: 'How is Humanistix different from other AI for HR platforms?',
      answer: `Humanistix differentiates through: (1) A 100% free quickscan completed in 4 minutes, (2) Real-time benchmarking against ${participantCount} organizations in your sector, (3) An extensive use case hub with practical implementation guides, (4) Focus on the European market with EU AI Act and GDPR compliance, and (5) Guidance from certified AI trainers with hands-on workshop experience. We believe every HR team should have access to AI knowledge, which is why our basic quickscan is always free.`
    },
    {
      question: 'Is AI in HR safe and ethically responsible?',
      answer: 'AI in HR can be applied safely and ethically when organizations have a solid governance framework. This includes: clear AI policy with guidelines for responsible use, "human in the loop" for important decisions like hiring and termination, transparency about when AI is used, compliance with the EU AI Act which classifies HR applications as "high-risk", and regular audits for bias and discrimination. Humanistix helps organizations establish this framework through our governance quickscans and workshops.'
    },
    {
      question: 'How much does it cost to implement AI in HR?',
      answer: 'AI implementation costs in HR vary widely: simple chatbots start at €50-200/month, while enterprise recruitment AI can cost €500-2000/month. However, the ROI is significant: organizations report an average 30-50% time savings on recruitment, 25% reduction in turnover through better matching, and 40% faster onboarding. The Humanistix quickscan is free and helps you determine which AI investments will yield the highest returns for your specific situation.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Generate FAQ Schema markup for SEO/GEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section className="py-16 bg-gray-50" id="faq">
      {/* FAQ Schema Script */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {language === 'nl' ? 'Veelgestelde vragen over AI voor HR' : 'Frequently Asked Questions about AI for HR'}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'nl' 
              ? 'Alles wat u moet weten over kunstmatige intelligentie in human resources'
              : 'Everything you need to know about artificial intelligence in human resources'}
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={faq.question}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              itemScope
              itemType="https://schema.org/Question"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                aria-expanded={openIndex === index}
              >
                <h3 
                  className="text-lg font-semibold text-gray-900 pr-4"
                  itemProp="name"
                >
                  {faq.question}
                </h3>
                {openIndex === index ? (
                  <ChevronUp className="h-5 w-5 text-blue-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                )}
              </button>
              
              {openIndex === index && (
                <div 
                  className="px-6 pb-5"
                  itemScope
                  itemType="https://schema.org/Answer"
                  itemProp="acceptedAnswer"
                >
                  <p 
                    className="text-gray-600 leading-relaxed"
                    itemProp="text"
                  >
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
